/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback,useRef} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import DataTable from '../components/common/DataTable';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import {fmtDate,fmtDateTime} from '../utils/helpers';
import {useTranslation} from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────
type NodeType='TRIGGER'|'CONDITION'|'ACTION'|'TIMER'|'END';
interface WfNode{id:string;type:NodeType;label:string;x:number;y:number;[k:string]:any;}
interface WfEdge{from:string;to:string;}
interface Workflow{id:string;name:string;description:string;category:string;triggerEvent:string;triggerModule:string;isActive:boolean;isPublished:boolean;nodes:WfNode[];edges:WfEdge[];version:number;stats?:any;}

// ─── Constants ────────────────────────────────────────────────────────────────
const NODE_COLORS:Record<NodeType,string>={TRIGGER:'#3b82f6',CONDITION:'#f59e0b',ACTION:'#10b981',TIMER:'#8b5cf6',END:'#ef4444'};
const NODE_ICONS:Record<NodeType,string>={TRIGGER:'⚡',CONDITION:'🔀',ACTION:'⚙️',TIMER:'⏱️',END:'🏁'};
const PRIO_CSS:Record<string,string>={URGENT:'bg-red-600 text-white',HIGH:'bg-red-100 text-red-700',NORMAL:'bg-slate-100 text-slate-600',LOW:'bg-slate-50 text-slate-400'};
const STATUS_CSS:Record<string,string>={RUNNING:'bg-blue-100 text-[#02357A]',COMPLETED:'bg-green-100 text-green-700',FAILED:'bg-red-100 text-red-700',WAITING:'bg-amber-100 text-amber-700',CANCELLED:'bg-slate-100 text-slate-400',OPEN:'bg-blue-100 text-[#02357A]',IN_PROGRESS:'bg-purple-100 text-purple-700',ESCALATED:'bg-red-200 text-red-800'};
const TRIGGER_EVENTS=['patient_registered','appointment_created','appointment_cancelled','admission_created','patient_discharged','lab_critical','lab_report_ready','prescription_created','bill_generated','payment_received','low_stock_alert','employee_joined','leave_submitted','bill_overdue','consent_signed'];
const ACTION_TYPES=['SEND_SMS','SEND_EMAIL','SEND_WHATSAPP','SEND_NOTIFICATION','CREATE_TASK','UPDATE_STATUS','CALL_WEBHOOK'];
const MODULES=['OPD','IPD','LAB','BILLING','INVENTORY','HR','PHARMACY','RADIOLOGY','NURSING'];
const CATEGORIES=['CLINICAL','FINANCIAL','ADMIN','HR','INVENTORY','CUSTOM'];

// ─── Visual Workflow Builder ──────────────────────────────────────────────────
function WorkflowCanvas({nodes,edges,onNodeClick,onAddEdge,selected}:{nodes:WfNode[];edges:WfEdge[];onNodeClick:(n:WfNode)=>void;onAddEdge:(from:string,to:string)=>void;selected:string|null}){
  const svgRef=useRef<SVGSVGElement>(null);
  const [dragging,setDragging]=useState<{nodeId:string;ox:number;oy:number}|null>(null);
  const [localNodes,setLocalNodes]=useState(nodes);
  const [edgeFrom,setEdgeFrom]=useState<string|null>(null);
  useEffect(()=>setLocalNodes(nodes),[nodes]);
  const W=140,H=60;

  const onMouseDown=(e:React.MouseEvent,nodeId:string)=>{
    if(e.button!==0)return;
    const svg=svgRef.current;if(!svg)return;
    const rect=svg.getBoundingClientRect();
    const node=localNodes.find(n=>n.id===nodeId)!;
    setDragging({nodeId,ox:e.clientX-rect.left-node.x,oy:e.clientY-rect.top-node.y});
    e.stopPropagation();
  };
  const onMouseMove=(e:React.MouseEvent)=>{
    if(!dragging||!svgRef.current)return;
    const rect=svgRef.current.getBoundingClientRect();
    setLocalNodes(prev=>prev.map(n=>n.id===dragging.nodeId?{...n,x:Math.max(0,e.clientX-rect.left-dragging.ox),y:Math.max(0,e.clientY-rect.top-dragging.oy)}:n));
  };
  const onMouseUp=()=>setDragging(null);

  const getEdgePath=(from:WfNode,to:WfNode)=>{
    const x1=from.x+W,y1=from.y+H/2;
    const x2=to.x,y2=to.y+H/2;
    const cx=(x1+x2)/2;
    return`M ${x1} ${y1} C ${cx} ${y1} ${cx} ${y2} ${x2} ${y2}`;
  };

  const maxX=Math.max(...localNodes.map(n=>n.x+W+40),800);
  const maxY=Math.max(...localNodes.map(n=>n.y+H+40),400);

  return(
    <div className="w-full overflow-auto border rounded-xl bg-slate-50" style={{height:420}}>
      <svg ref={svgRef} width={maxX} height={maxY} onMouseMove={onMouseMove} onMouseUp={onMouseUp} className="cursor-default">
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8"/>
          </marker>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)"/>
        {/* Edges */}
        {edges.map((e,i)=>{
          const from=localNodes.find(n=>n.id===e.from);
          const to=localNodes.find(n=>n.id===e.to);
          if(!from||!to)return null;
          return<path key={i} d={getEdgePath(from,to)} fill="none" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#arrow)"/>;
        })}
        {/* Edge-from indicator */}
        {edgeFrom&&<text x="10" y="20" fontSize="12" fill="#3b82f6">Click a node to connect from {edgeFrom} →</text>}
        {/* Nodes */}
        {localNodes.map(node=>{
          const color=NODE_COLORS[node.type]||'#64748b';
          const isSel=selected===node.id;
          const isEdgeSource=edgeFrom===node.id;
          return(
            <g key={node.id} transform={`translate(${node.x},${node.y})`}
              onMouseDown={e=>onMouseDown(e,node.id)}
              onClick={()=>{if(edgeFrom&&edgeFrom!==node.id){onAddEdge(edgeFrom,node.id);setEdgeFrom(null);}else if(!edgeFrom){onNodeClick(node);}}}>
              <rect width={W} height={H} rx={8} fill="white" stroke={isSel?color:'#e2e8f0'} strokeWidth={isSel?3:1.5} filter="drop-shadow(0 2px 4px rgba(0,0,0,.08))" style={{cursor:'grab'}}/>
              <rect width={W} height={10} rx={4} fill={color} ry={4}/>
              <rect width={W} height={4} fill={color} y={6}/>
              <text x={W/2} y={28} textAnchor="middle" fontSize={16}>{NODE_ICONS[node.type]}</text>
              <text x={W/2} y={44} textAnchor="middle" fontSize={11} fill="#1e293b" fontWeight={600}>{node.label.slice(0,18)}{node.label.length>18?'…':''}</text>
              {/* Connect button */}
              <circle cx={W} cy={H/2} r={8} fill={isEdgeSource?'#3b82f6':'#94a3b8'} style={{cursor:'crosshair'}}
                onClick={e=>{e.stopPropagation();setEdgeFrom(isEdgeSource?null:node.id);}}/>
              <text x={W} y={H/2+4} textAnchor="middle" fontSize={10} fill="white" style={{cursor:'crosshair',pointerEvents:'none'}}>→</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Node editor panel ────────────────────────────────────────────────────────
function NodePanel({node,onUpdate,onDelete,onClose}:{node:WfNode;onUpdate:(n:WfNode)=>void;onDelete:()=>void;onClose:()=>void}){
  const {t}=useTranslation();
  const[form,setForm]=useState({...node});
  return(
    <div className="border rounded-xl bg-white p-4 shadow-lg w-72 flex-shrink-0 space-y-3">
      <div className="flex justify-between items-center"><h4 className="font-bold flex items-center gap-2"><span>{NODE_ICONS[node.type]}</span>{node.type}</h4><button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button></div>
      <div><label className="text-xs text-slate-500 font-medium">Label</label><Input value={form.label} onChange={e=>setForm({...form,label:e.target.value})} className="text-sm"/></div>
      {node.type==='ACTION'&&(<>
        <div><label className="text-xs text-slate-500 font-medium">Action Type</label><Select value={form.actionType||''} onChange={e=>setForm({...form,actionType:e.target.value})} className="text-sm"><option value="">Select...</option>{ACTION_TYPES.map(a=><option key={a}>{a}</option>)}</Select></div>
        {(form.actionType==='CREATE_TASK')&&<>
          <div><label className="text-xs text-slate-500 font-medium">Task Name</label><Input value={form.taskName||''} onChange={e=>setForm({...form,taskName:e.target.value})} placeholder="e.g. Nursing Assessment" className="text-sm"/></div>
          <div><label className="text-xs text-slate-500 font-medium">Assign to Role</label><Input value={form.role||''} onChange={e=>setForm({...form,role:e.target.value})} placeholder="NURSE, BILLING, DOCTOR..." className="text-sm"/></div>
          <div><label className="text-xs text-slate-500 font-medium">SLA (minutes)</label><Input type="number" value={form.slaMinutes||''} onChange={e=>setForm({...form,slaMinutes:+e.target.value})} placeholder="30" className="text-sm"/></div>
        </>}
        {(form.actionType||'').includes('NOTIFICATION')||((form.actionType||'').includes('SMS'))||((form.actionType||'').includes('WHATSAPP'))||((form.actionType||'').includes('EMAIL'))?<>
          <div><label className="text-xs text-slate-500 font-medium">Template Code</label><Input value={form.template||''} onChange={e=>setForm({...form,template:e.target.value})} placeholder="APPT_REMINDER" className="text-sm"/></div>
          <div><label className="text-xs text-slate-500 font-medium">Target Role</label><Input value={form.role||''} onChange={e=>setForm({...form,role:e.target.value})} placeholder="PATIENT, DOCTOR, NURSE..." className="text-sm"/></div>
        </>:null}
        {form.actionType==='CALL_WEBHOOK'&&<div><label className="text-xs text-slate-500 font-medium">Webhook URL</label><Input value={form.webhookUrl||''} onChange={e=>setForm({...form,webhookUrl:e.target.value})} placeholder="https://..." className="text-sm"/></div>}
      </>)}
      {node.type==='CONDITION'&&(<>
        <div><label className="text-xs text-slate-500 font-medium">Field</label><Input value={form.field||''} onChange={e=>setForm({...form,field:e.target.value})} placeholder="e.g. department" className="text-sm"/></div>
        <div><label className="text-xs text-slate-500 font-medium">Operator</label><Select value={form.operator||'='} onChange={e=>setForm({...form,operator:e.target.value})} className="text-sm"><option>=</option><option>!=</option><option>{'>'}</option><option>{'<'}</option><option>{'>='}</option><option>{'<='}</option><option>contains</option></Select></div>
        <div><label className="text-xs text-slate-500 font-medium">Value</label><Input value={form.value||''} onChange={e=>setForm({...form,value:e.target.value})} placeholder="ICU" className="text-sm"/></div>
      </>)}
      {node.type==='TIMER'&&(<>
        <div><label className="text-xs text-slate-500 font-medium">Wait (minutes)</label><Input type="number" value={form.waitMinutes||''} onChange={e=>setForm({...form,waitMinutes:+e.target.value})} placeholder="60" className="text-sm"/></div>
      </>)}
      <div className="flex gap-2 pt-1">
        <button onClick={()=>onUpdate(form)} className="btn-primary text-xs flex-1">Save Node</button>
        <button onClick={onDelete} className="btn-secondary text-xs border-red-200 text-red-500 hover:bg-red-50">{t('common.delete','Delete')}</button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WorkflowPage(){
  const {t}=useTranslation();
  const [tab,setTab]=useState<'dashboard'|'workflows'|'builder'|'tasks'|'instances'|'sla'>('dashboard');
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState(false);
  const [dash,setDash]=useState<any>({});
  const [workflows,setWorkflows]=useState<Workflow[]>([]);
  const [tasks,setTasks]=useState<any[]>([]);
  const [taskTotal,setTaskTotal]=useState(0);
  const [instances,setInstances]=useState<any[]>([]);
  const [instTotal,setInstTotal]=useState(0);
  const [slaData,setSlaData]=useState<any[]>([]);

  // Builder state
  const [editingWf,setEditingWf]=useState<Workflow|null>(null);
  const [builderNodes,setBuilderNodes]=useState<WfNode[]>([]);
  const [builderEdges,setBuilderEdges]=useState<WfEdge[]>([]);
  const [selectedNode,setSelectedNode]=useState<WfNode|null>(null);
  const [wfMeta,setWfMeta]=useState<any>({name:'',description:'',category:'CLINICAL',triggerEvent:'appointment_created',triggerModule:'OPD',
    triggerConditions:'',
    publishedBy:'',
    publishedAt:'',
    instanceId:'',
    actionConfig:'',
    inputData:'',
    outputData:'',
    slaHours:'',
    escalationLevel:'',
    autoResolve:false
  });

  // Filters
  const [taskStatus,setTaskStatus]=useState('OPEN');
  const [catFilter,setCatFilter]=useState('');
  const [instStatus,setInstStatus]=useState('');

  // Instance detail
  const [selInstance,setSelInstance]=useState<any>(null);
  const [showInstDetail,setShowInstDetail]=useState(false);

  // Test trigger
  const [showTrigger,setShowTrigger]=useState(false);
  const [triggerData,setTriggerData]=useState<any>({event:'appointment_created',data:JSON.stringify({patient_name:'John Doe',department:'OPD',doctor_name:'Dr. Sharma'},null,2)});

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),5000);};

  useEffect(()=>{
    api.getWorkflowDashboard().then(r=>setDash(r.data||{})).catch(()=>{});
    api.getWorkflows().then(r=>setWorkflows(r.data||[])).catch(()=>{});
  },[]);

  const loadTab=useCallback(async()=>{
    setLoading(true);
    try{
      if(tab==='tasks'){const r=await api.getWorkflowTasks({status:taskStatus,take:100});setTasks(r.data?.data||[]);setTaskTotal(r.data?.total||0);}
      if(tab==='instances'){const r=await api.getWorkflowInstances({status:instStatus||undefined,take:100});setInstances(r.data?.data||[]);setInstTotal(r.data?.total||0);}
      if(tab==='sla'){const r=await api.getSLAReport();setSlaData(r.data||[]);}
      if(tab==='workflows'){const r=await api.getWorkflows({category:catFilter||undefined});setWorkflows(r.data||[]);}
    }catch{}finally{setLoading(false);}
  },[tab,taskStatus,catFilter,instStatus]);
  useEffect(()=>{const t=setTimeout(loadTab,300);return()=>clearTimeout(t);},[loadTab]);

  // Builder helpers
  const openBuilder=(wf?:Workflow)=>{
    if(wf){setEditingWf(wf);setBuilderNodes(wf.nodes||[]);setBuilderEdges(wf.edges||[]);setWfMeta({name:wf.name,description:wf.description||'',category:wf.category,triggerEvent:wf.triggerEvent,triggerModule:wf.triggerModule});}
    else{setEditingWf(null);setBuilderNodes([{id:'t1',type:'TRIGGER',label:'Trigger',x:50,y:160},{id:'e1',type:'END',label:'Done',x:600,y:160}]);setBuilderEdges([]);setWfMeta({name:'',description:'',category:'CLINICAL',triggerEvent:'appointment_created',triggerModule:'OPD'});}
    setSelectedNode(null);setTab('builder');
  };

  const addNode=(type:NodeType)=>{
    const id=`${type.toLowerCase()[0]}${Date.now()}`;
    const lastNode=builderNodes[builderNodes.length-2]||{x:50,y:160};
    const endNode=builderNodes.find(n=>n.type==='END');
    const endIdx=builderNodes.findIndex(n=>n.type==='END');
    const newNode:WfNode={id,type,label:type==='ACTION'?'New Action':type==='CONDITION'?'Check Condition':type==='TIMER'?'Wait':type,x:Math.max(lastNode.x+200,200),y:160,actionType:type==='ACTION'?'SEND_NOTIFICATION':undefined};
    if(endNode){
      setBuilderNodes(prev=>{const arr=[...prev];arr.splice(endIdx,0,newNode);return arr;});
      // Auto-wire to end
      const prevNode=builderNodes[endIdx-1]||builderNodes[0];
      setBuilderEdges(prev=>[...prev.filter(e=>e.to!==endNode.id&&e.from!==prevNode.id),{from:prevNode.id,to:id},{from:id,to:endNode.id}]);
    } else {
      setBuilderNodes(prev=>[...prev,newNode]);
    }
  };

  const updateNode=(updated:WfNode)=>{setBuilderNodes(prev=>prev.map(n=>n.id===updated.id?updated:n));setSelectedNode(updated);};
  const deleteNode=(id:string)=>{setBuilderNodes(prev=>prev.filter(n=>n.id!==id));setBuilderEdges(prev=>prev.filter(e=>e.from!==id&&e.to!==id));setSelectedNode(null);};
  const addEdge=(from:string,to:string)=>{if(!builderEdges.find(e=>e.from===from&&e.to===to))setBuilderEdges(prev=>[...prev,{from,to}]);};

  const saveWorkflow=async()=>{
    if(!wfMeta.name){notify('❌ Workflow name is required');return;}
    setSaving(true);
    try{
      const payload={...wfMeta,nodes:builderNodes,edges:builderEdges};
      if(editingWf){await api.updateWorkflow(editingWf.id,payload);notify('✅ Workflow saved!');}
      else{await api.createWorkflow(payload);notify('✅ Workflow created!');}
      const r=await api.getWorkflows();setWorkflows(r.data||[]);setTab('workflows');
    }catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const testTrigger=async()=>{
    setSaving(true);
    try{let data={};try{data=JSON.parse(triggerData.data);}catch{}const r=await api.triggerWorkflow({event:triggerData.event,data});notify(`✅ Triggered ${r.data?.length||0} workflow(s)!`);setShowTrigger(false);api.getWorkflowDashboard().then(x=>setDash(x.data||{}));loadTab();}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const TABS=[['dashboard','📊 Dashboard'],['workflows','⚙️ Workflows'],['builder','🎨 Builder'],['tasks','✅ Tasks'],['instances','▶ Instances'],['sla','📏 SLA']];

  return(
    <Layout title="Workflow Engine" actions={
      <div className="flex gap-2">
        <button onClick={()=>setShowTrigger(true)} className="btn-secondary text-sm">⚡ Test Trigger</button>
        <button onClick={()=>openBuilder()} className="btn-primary text-sm">+ New Workflow</button>
      </div>
    }>
      {msg&&<div className={`mb-3 p-2.5 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Active Workflows" value={dash.activeWorkflows||0} icon="⚙️" color="blue"/>
        <StatCard label="Running Now" value={dash.runningInstances||0} icon="▶" color="green"/>
        <StatCard label="Open Tasks" value={dash.openTasks||0} icon="✅" color="amber"/>
        <StatCard label="SLA Breached" value={dash.slaBreached||0} icon="⚠️" color="red"/>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Completed Today" value={dash.completedToday||0} icon="🏁" color="teal"/>
        <StatCard label="Failed Today" value={dash.failedToday||0} icon="❌" color="red"/>
        <StatCard label="Total Workflows" value={dash.totalWorkflows||0} icon="📋" color="slate"/>
        <StatCard label="Urgent Tasks" value={dash.urgentTasks||0} icon="🚨" color="red"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b">
        {TABS.map(([t,l])=><button key={t} onClick={()=>setTab(t as any)} className={`px-3 py-1.5 rounded-t-lg text-xs font-medium whitespace-nowrap flex-shrink-0 ${tab===t?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}${t==='tasks'&&dash.urgentTasks?' ring-2 ring-red-400':''}`}>{l}</button>)}
      </div>

      {/* ════ DASHBOARD ════ */}
      {tab==='dashboard'&&(
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Top workflows */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold">Top Workflows (7 days)</h3></div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {(dash.topWorkflows||[]).map((w:any)=>(
                  <div key={w.name} className="p-3 flex items-center gap-3">
                    <span className={`w-2 h-10 rounded flex-shrink-0 ${w.category==='CLINICAL'?'bg-blue-400':w.category==='FINANCIAL'?'bg-green-400':w.category==='HR'?'bg-purple-400':'bg-amber-400'}`}/>
                    <div className="flex-1"><p className="font-semibold text-sm">{w.name}</p><p className="text-xs text-slate-400">{w.category}</p></div>
                    <div className="text-right"><p className="font-bold text-[#02357A]">{w.runs} runs</p><p className="text-xs text-slate-400">{w.completed} OK</p></div>
                  </div>
                ))}
                {!dash.topWorkflows?.length&&<p className="p-4 text-sm text-slate-400">No data yet</p>}
              </div>
            </div>

            {/* Status breakdown */}
            <div className="card p-4">
              <h3 className="font-semibold mb-3">Execution Status (7 days)</h3>
              {(dash.byStatus||[]).map((s:any)=>{
                const total=+(dash.byStatus||[]).reduce((a:any,b:any)=>a+(+b.cnt),0)||1;
                const pct=Math.round(+s.cnt/total*100);
                return<div key={s.status} className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold w-24 flex-shrink-0 text-center ${STATUS_CSS[s.status]||''}`}>{s.status}</span>
                  <div className="flex-1 bg-slate-100 rounded h-2"><div className={`h-2 rounded ${s.status==='COMPLETED'?'bg-green-500':s.status==='FAILED'?'bg-red-500':s.status==='RUNNING'?'bg-blue-500':'bg-amber-500'}`} style={{width:`${pct}%`}}/></div>
                  <span className="text-xs text-slate-500 w-8 text-right">{s.cnt}</span>
                </div>;
              })}
              {!dash.byStatus?.length&&<p className="text-sm text-slate-400">No executions yet</p>}
            </div>

            {/* By category */}
            <div className="card p-4">
              <h3 className="font-semibold mb-3">Workflows by Category</h3>
              <div className="space-y-2">
                {(dash.byCategory||[]).map((c:any)=>(
                  <div key={c.category} className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><span className={`w-3 h-3 rounded-full ${c.category==='CLINICAL'?'bg-blue-400':c.category==='FINANCIAL'?'bg-green-400':c.category==='HR'?'bg-purple-400':c.category==='INVENTORY'?'bg-amber-400':'bg-slate-400'}`}/><span className="text-sm">{c.category}</span></div>
                    <span className="font-bold text-sm">{c.cnt}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t space-y-1">
                {(dash.recentActivity||[]).map((a:any)=>(
                  <div key={a.id} className="flex items-center gap-2 text-xs"><span className={`px-1.5 py-0.5 rounded ${STATUS_CSS[a.status]||''}`}>{a.status}</span><span className="flex-1 truncate text-slate-500">{a.workflowName||'—'}</span><span className="text-slate-400">{fmtDateTime(a.startedAt)}</span></div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick prebuilt workflows */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center"><h3 className="font-semibold">✨ Pre-built Workflow Templates</h3><button onClick={()=>setTab('workflows')} className="text-xs text-[#02357A] hover:underline">View all →</button></div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[{name:'Appointment Reminder',icon:'📅',cat:'CLINICAL',trigger:'appointment_created'},{name:'Critical Lab Alert',icon:'🚨',cat:'CLINICAL',trigger:'lab_critical'},{name:'Admission Checklist',icon:'🏥',cat:'CLINICAL',trigger:'admission_created'},{name:'Low Stock Auto-PR',icon:'📦',cat:'INVENTORY',trigger:'low_stock_alert'},{name:'Employee Onboarding',icon:'👤',cat:'HR',trigger:'employee_joined'},{name:'Discharge Follow-up',icon:'🏠',cat:'CLINICAL',trigger:'patient_discharged'},{name:'Insurance Pre-auth',icon:'📋',cat:'FINANCIAL',trigger:'admission_created'},{name:'Bill Reminder',icon:'💰',cat:'FINANCIAL',trigger:'bill_overdue'}].map(t=>(
                <div key={t.name} className="bg-slate-50 rounded-xl p-3 text-center hover:bg-blue-50 hover:border-blue-200 border border-slate-200 cursor-pointer" onClick={()=>setTab('workflows')}>
                  <p className="text-2xl">{t.icon}</p><p className="text-xs font-semibold mt-1">{t.name}</p><span className={`text-xs px-1.5 py-0.5 rounded mt-1 inline-block ${t.cat==='CLINICAL'?'bg-blue-100 text-[#02357A]':t.cat==='FINANCIAL'?'bg-green-100 text-green-700':t.cat==='HR'?'bg-purple-100 text-purple-700':'bg-amber-100 text-amber-700'}`}>{t.cat}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════ WORKFLOWS LIST ════ */}
      {tab==='workflows'&&(
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap items-center">
            {['','CLINICAL','FINANCIAL','ADMIN','HR','INVENTORY','CUSTOM'].map(c=><button key={c} onClick={()=>setCatFilter(c)} className={`text-xs px-3 py-1.5 rounded-full border font-medium ${catFilter===c?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{c||'All'}</button>)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflows.map(wf=>(
              <div key={wf.id} className={`card p-4 border-2 ${wf.isActive?'border-green-300':wf.isPublished?'border-blue-200':'border-slate-200'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div><p className="font-bold">{wf.name}</p><p className="text-xs text-slate-400">{wf.description?.slice(0,60)||'No description'}</p></div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {wf.isActive&&<Badge label="ACTIVE" className="bg-green-100 text-green-700"/>}
                    {!wf.isActive&&wf.isPublished&&<Badge label="PAUSED" className="bg-amber-100 text-amber-700"/>}
                    {!wf.isPublished&&<Badge label="DRAFT" className="bg-slate-100 text-slate-500"/>}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap mb-3">
                  <Badge label={wf.category} className="bg-blue-50 text-[#02357A] text-xs"/>
                  <Badge label={wf.triggerModule} className="bg-slate-100 text-slate-600 text-xs"/>
                  <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{wf.triggerEvent?.replace(/_/g,' ')}</span>
                  <span className="text-xs text-slate-400 ml-auto">v{wf.version}</span>
                </div>
                {wf.stats&&<div className="flex gap-3 text-xs text-slate-400 mb-3"><span>▶ {wf.stats.runs||0} runs</span><span>✅ {wf.stats.completed||0} ok</span>{+wf.stats.failed>0&&<span className="text-red-500">❌ {wf.stats.failed} failed</span>}</div>}
                <div className="flex gap-2">
                  <button onClick={()=>openBuilder(wf)} className="text-xs text-[#02357A] hover:underline font-medium">{t('common.edit','Edit')}</button>
                  <button onClick={async()=>{if(wf.isActive){await api.unpublishWorkflow(wf.id);notify('⏸ Paused');}else{await api.publishWorkflow(wf.id);notify('✅ Published & Active!');}loadTab();api.getWorkflowDashboard().then(r=>setDash(r.data||{}));}} className={`text-xs font-medium hover:underline ${wf.isActive?'text-amber-600':'text-green-600'}`}>{wf.isActive?'⏸ Pause':'▶ Publish'}</button>
                  <button onClick={async()=>{await api.cloneWorkflow(wf.id);notify('✅ Cloned!');loadTab();}} className="text-xs text-slate-400 hover:underline">Clone</button>
                  <button onClick={async()=>{const r=await api.triggerWorkflow({event:wf.triggerEvent,data:{workflow_test:true}});notify(`⚡ Test ran — ${r.data?.length||0} instance(s) started!`);loadTab();}} className="text-xs text-purple-500 hover:underline">Test ⚡</button>
                </div>
              </div>
            ))}
            {!workflows.length&&<p className="col-span-2 text-center text-slate-400 py-8">No workflows. Click "New Workflow" to create one.</p>}
          </div>
        </div>
      )}

      {/* ════ WORKFLOW BUILDER ════ */}
      {tab==='builder'&&(
        <div className="space-y-3">
          {/* Metadata bar */}
          <div className="card p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="col-span-2"><label className="text-xs text-slate-500 font-medium">Workflow Name *</label><Input value={wfMeta.name} onChange={e=>setWfMeta({...wfMeta,name:e.target.value})} placeholder="e.g. Patient Admission Checklist" className="text-sm"/></div>
              <div><label className="text-xs text-slate-500 font-medium">{t('common.category','Category')}</label><Select value={wfMeta.category} onChange={e=>setWfMeta({...wfMeta,category:e.target.value})} className="text-sm">{CATEGORIES.map(c=><option key={c}>{c}</option>)}</Select></div>
              <div><label className="text-xs text-slate-500 font-medium">Trigger Event</label><Select value={wfMeta.triggerEvent} onChange={e=>setWfMeta({...wfMeta,triggerEvent:e.target.value})} className="text-sm">{TRIGGER_EVENTS.map(t=><option key={t}>{t}</option>)}</Select></div>
              <div><label className="text-xs text-slate-500 font-medium">Source Module</label><Select value={wfMeta.triggerModule} onChange={e=>setWfMeta({...wfMeta,triggerModule:e.target.value})} className="text-sm">{MODULES.map(m=><option key={m}>{m}</option>)}</Select></div>
            </div>
          </div>

          {/* Node palette */}
          <div className="card p-3 flex gap-2 flex-wrap items-center">
            <span className="text-xs text-slate-400 font-semibold mr-2">ADD NODE:</span>
            {(['ACTION','CONDITION','TIMER'] as NodeType[]).map(t=>(
              <button key={t} onClick={()=>addNode(t)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border-2 font-medium hover:shadow-sm" style={{borderColor:NODE_COLORS[t],color:NODE_COLORS[t],background:NODE_COLORS[t]+'18'}}>
                <span>{NODE_ICONS[t]}</span> {t}
              </button>
            ))}
            <span className="text-xs text-slate-300 mx-2">|</span>
            <span className="text-xs text-slate-400">Click orange → button on a node to connect it</span>
          </div>

          {/* Canvas + panel */}
          <div className="flex gap-4 items-start">
            <div className="flex-1 min-w-0">
              <WorkflowCanvas nodes={builderNodes} edges={builderEdges} onNodeClick={n=>setSelectedNode(n)} onAddEdge={addEdge} selected={selectedNode?.id||null}/>
            </div>
            {selectedNode&&<NodePanel node={selectedNode} onUpdate={updateNode} onDelete={()=>deleteNode(selectedNode.id)} onClose={()=>setSelectedNode(null)}/>}
          </div>

          {/* Edges list */}
          {builderEdges.length>0&&<div className="card p-3"><p className="text-xs font-semibold text-slate-500 mb-2">CONNECTIONS ({builderEdges.length})</p><div className="flex gap-2 flex-wrap">{builderEdges.map((e,i)=><span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded-full">{e.from} → {e.to}<button onClick={()=>setBuilderEdges(prev=>prev.filter((_,idx)=>idx!==i))} className="ml-1 text-red-400 hover:text-red-600">✕</button></span>)}</div></div>}

          <div className="flex gap-3"><button onClick={saveWorkflow} disabled={saving} className="btn-primary">{saving?'Saving...':editingWf?'💾 Save Changes':'✨ Create Workflow'}</button><button onClick={()=>setTab('workflows')} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </div>
      )}

      {/* ════ TASKS ════ */}
      {tab==='tasks'&&(
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            {['OPEN','IN_PROGRESS','COMPLETED','ESCALATED'].map(s=><button key={s} onClick={()=>setTaskStatus(s)} className={`text-xs px-3 py-1.5 rounded-full border font-medium ${taskStatus===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{s.replace('_',' ')}</button>)}
            <button onClick={async()=>{const r=await api.checkWorkflowSLA();notify(`⚠️ SLA check: ${r.data?.breached||0} tasks breached`);loadTab();}} className="text-xs border border-amber-300 text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-full ml-auto">⚠️ Check SLA</button>
          </div>
          <p className="text-xs text-slate-400 pl-1">{taskTotal} tasks</p>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No tasks" emptyIcon="✅" data={tasks} columns={[
              {key:'task',label:'Task',render:(r:any)=><div><p className="font-semibold text-sm">{r.taskName}</p>{r.description&&<p className="text-xs text-slate-400 line-clamp-1">{r.description}</p>}</div>},
              {key:'type',label:'Type',render:(r:any)=><Badge label={r.taskType||'GENERAL'} className="bg-slate-100 text-slate-600"/>},
              {key:'priority',label:'Priority',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-bold ${PRIO_CSS[r.priority]||''}`}>{r.priority}</span>},
              {key:'role',label:'Assigned To',render:(r:any)=><span className="text-sm">{r.assignedRole||r.assignedTo||'—'}</span>},
              {key:'due',label:'Due',render:(r:any)=>{const dueClass=r.slaBreaching?'text-red-600 font-bold':r.dueAt?'text-slate-400':'text-slate-300';return<span className={`text-xs ${dueClass}`}>{r.dueAt?fmtDateTime(r.dueAt):'—'}{r.slaBreaching&&' ⚠️'}</span>;}},
              {key:'sla',label:'SLA',render:(r:any)=>r.slaMinutes?<span className="text-xs">{r.slaMinutes} min</span>:<span className="text-slate-300 text-xs">—</span>},
              {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_CSS[r.status]||''}`}>{r.status?.replace('_',' ')}</span>},
              {key:'actions',label:'',render:(r:any)=>(
                <div className="flex gap-1.5">
                  {r.status==='OPEN'&&<button onClick={async()=>{await api.startWorkflowTask(r.id);notify('✅ Started');loadTab();}} className="text-xs text-[#02357A] hover:underline">{t('common.start','Start')}</button>}
                  {r.status==='IN_PROGRESS'&&<button onClick={async()=>{const n=prompt('Completion notes:');if(n!=null){await api.completeWorkflowTask(r.id,{notes:n});notify('✅ Completed!');loadTab();}}} className="text-xs text-green-600 hover:underline">{t('common.complete','Complete')}</button>}
                  {['OPEN','IN_PROGRESS'].includes(r.status)&&<button onClick={async()=>{await api.escalateWorkflowTask(r.id,{reason:'Manual escalation'});notify('⚠️ Escalated');loadTab();}} className="text-xs text-red-400 hover:underline">Escalate</button>}
                </div>
              )},
            ]}/>
          </div>
        </div>
      )}

      {/* ════ INSTANCES ════ */}
      {tab==='instances'&&(
        <div className="space-y-3">
          <div className="flex gap-2">
            {['','RUNNING','COMPLETED','FAILED','WAITING'].map(s=><button key={s} onClick={()=>setInstStatus(s)} className={`text-xs px-3 py-1.5 rounded-full border font-medium ${instStatus===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{s||'All'}</button>)}
          </div>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No workflow executions" emptyIcon="▶" data={instances} columns={[
              {key:'wf',label:'Workflow',render:(r:any)=><div><p className="font-semibold text-sm">{r.workflowName||r.workflowId?.slice(-8)||'—'}</p><p className="text-xs text-slate-400">{r.triggerEvent?.replace(/_/g,' ')||'—'}</p></div>},
              {key:'entity',label:'Entity',render:(r:any)=><div><p className="text-xs">{r.entityType||'—'}</p><p className="font-mono text-xs text-slate-400">{r.entityId?.slice(-8)||'—'}</p></div>},
              {key:'started',label:'Started',render:(r:any)=><span className="text-xs">{fmtDateTime(r.startedAt)}</span>},
              {key:'node',label:'Current Node',render:(r:any)=><span className="text-xs font-mono text-slate-400">{r.currentNodeId||'—'}</span>},
              {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_CSS[r.status]||''}`}>{r.status}</span>},
              {key:'path',label:'Steps',render:(r:any)=><span className="text-xs text-slate-400">{r.executionPath?.length||0}</span>},
              {key:'actions',label:'',render:(r:any)=><button onClick={async()=>{const d=await api.getWorkflowInstance(r.id);setSelInstance(d.data);setShowInstDetail(true);}} className="text-xs text-[#02357A] hover:underline">{t('common.view','View')}</button>},
            ]}/>
          </div>
        </div>
      )}

      {/* ════ SLA ════ */}
      {tab==='sla'&&(
        <div className="space-y-4">
          <div className="card overflow-hidden">
            <div className="p-4 border-b"><h3 className="font-semibold">SLA Performance (30 days)</h3></div>
            {!slaData.length?<p className="p-4 text-sm text-slate-400">No SLA data yet. Tasks with SLA configured will appear here.</p>:
            <div className="divide-y">{slaData.map((s:any,i:number)=>{
              const onTimePct=s.total_tasks>0?Math.round(s.on_time/s.total_tasks*100):0;
              return<div key={i} className="p-4 flex items-center gap-4">
                <div className="flex-1"><p className="font-semibold">{s.workflow_name||'Unknown'}</p><p className="text-xs text-slate-400">{s.total_tasks} tasks · avg {Math.round(s.avg_minutes||0)} min</p></div>
                <div className="flex gap-4 text-xs">
                  <div className="text-center"><p className="font-bold text-green-600">{s.on_time}</p><p className="text-slate-400">On Time</p></div>
                  <div className="text-center"><p className={`font-bold ${+s.breached>0?'text-red-600':'text-slate-300'}`}>{s.breached||0}</p><p className="text-slate-400">Breached</p></div>
                </div>
                <div className="w-24">
                  <div className="w-full bg-slate-100 rounded h-2"><div className={`h-2 rounded ${onTimePct>=90?'bg-green-500':onTimePct>=70?'bg-amber-500':'bg-red-500'}`} style={{width:`${onTimePct}%`}}/></div>
                  <p className={`text-xs text-center mt-0.5 font-bold ${onTimePct>=90?'text-green-600':onTimePct>=70?'text-amber-600':'text-red-600'}`}>{onTimePct}%</p>
                </div>
              </div>;
            })}</div>}
          </div>
        </div>
      )}

      {/* ════ INSTANCE DETAIL MODAL ════ */}
      <SlidePanel isOpen={showInstDetail} onClose={()=>setShowInstDetail(false)} title="Workflow Execution Detail" size="xl">
        {selInstance&&(
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 flex items-start justify-between">
              <div><p className="font-black text-lg">{selInstance.workflowName||'—'}</p><p className="text-sm text-slate-400">{selInstance.triggerEvent?.replace(/_/g,' ')} · {fmtDateTime(selInstance.startedAt)}</p></div>
              <Badge label={selInstance.status} className={STATUS_CSS[selInstance.status]||''}/>
            </div>
            {/* Execution log */}
            <div>
              <h4 className="font-semibold mb-2 text-sm">Execution Log</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {(selInstance.logs||[]).map((l:any,i:number)=>(
                  <div key={i} className={`flex items-start gap-3 p-2 rounded text-xs ${l.status==='FAILED'?'bg-red-50':l.status==='WAITING'?'bg-amber-50':'bg-green-50'}`}>
                    <span>{l.status==='SUCCESS'||l.status==='COMPLETED'?'✅':l.status==='FAILED'?'❌':l.status==='WAITING'?'⏳':'⏭️'}</span>
                    <div className="flex-1"><span className="font-semibold">{l.nodeName||l.nodeId}</span><span className="text-slate-400 ml-2">({l.nodeType})</span>{l.message&&<p className="text-slate-500 mt-0.5">{l.message}</p>}</div>
                    <span className="text-slate-400">{l.durationMs}ms</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Actions */}
            {selInstance.actions?.length>0&&<div>
              <h4 className="font-semibold mb-2 text-sm">Actions Executed</h4>
              <div className="space-y-1">{selInstance.actions.map((a:any,i:number)=>(
                <div key={i} className="flex items-center gap-3 text-xs p-2 bg-slate-50 rounded">
                  <Badge label={a.actionType} className="bg-blue-50 text-[#02357A]"/>
                  <span className="text-slate-400">{fmtDateTime(a.executedAt)}</span>
                  {a.result?.demo&&<Badge label="DEMO" className="bg-yellow-100 text-yellow-700"/>}
                  <Badge label={a.status} className={STATUS_CSS[a.status]||''} />
                </div>
              ))}</div>
            </div>}
            {/* Tasks created */}
            {selInstance.tasks?.length>0&&<div>
              <h4 className="font-semibold mb-2 text-sm">Tasks Created</h4>
              <div className="space-y-1">{selInstance.tasks.map((t:any)=>(
                <div key={t.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs"><span className="font-medium">{t.taskName}</span><div className="flex gap-2"><span className="text-slate-400">{t.assignedRole||'—'}</span><Badge label={t.status} className={STATUS_CSS[t.status]||''}/></div></div>
              ))}</div>
            </div>}
            {selInstance.errorMessage&&<div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">❌ Error: {selInstance.errorMessage}</div>}
          </div>
        )}
      </SlidePanel>

      {/* ════ TEST TRIGGER MODAL ════ */}
      <SlidePanel isOpen={showTrigger} onClose={()=>setShowTrigger(false)} title="⚡ Test Workflow Trigger" size="md">
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-[#02357A]">Fire a trigger event manually to test workflows. All active workflows listening to this event will execute.</div>
          <div><label className="label">Trigger Event *</label><Select value={triggerData.event} onChange={e=>setTriggerData({...triggerData,event:e.target.value})}>{TRIGGER_EVENTS.map(t=><option key={t}>{t}</option>)}</Select></div>
          <div><label className="label">Event Data (JSON)</label><textarea className="input font-mono text-xs" rows={6} value={triggerData.data} onChange={e=>setTriggerData({...triggerData,data:e.target.value})}/></div>
          <div className="flex gap-3"><button onClick={testTrigger} disabled={saving} className="btn-primary flex-1">{saving?'Triggering...':'⚡ Fire Trigger'}</button><button onClick={()=>setShowTrigger(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
